import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { RobotoIconButton } from "@/shared/components/RobotoIconButton";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { AccountTreeOutlinedIcon } from "@/shared/foundation/icons/mui";

import { KnowledgeGraphSection } from "./KnowledgeGraphSection";
import styles from "./MessageKnowledgeGraphButton.module.css";

const LABEL = "Knowledge graph retrieval for this turn";

interface MessageKnowledgeGraphButtonProps {
  threadId: string;
  /** Sequence numbers of every assistant message this turn produced, in
   *  order — the turn's traces are spread across them, one message per
   *  Bedrock call (see `turnContextMapping`). Reconstructing each one's
   *  prompt is how its traces are reached, even though this control renders
   *  on the user message. */
  assistantSeqsOfTurn: number[];
}

/**
 * Admin-only icon button rendered under a user message: opens every
 * knowledge-graph retrieval that ran for this turn — the passive per-turn
 * injection and each `query_knowledge` tool call. Sits alongside
 * Fork/Feedback/Debug in the same action-icon row style, but on the user side
 * of the exchange rather than the assistant's — mirrors `MessageDebugButton`,
 * but a user message has no Bedrock invocation of its own to reconstruct, so
 * this fetches the prompt of each assistant message the turn produced and
 * pulls just the traces out of them.
 *
 * The fetches are lazy — gated on the dialog being open — so browsing the
 * thread never front-loads every turn's reconstruction. They deliberately
 * reuse `useChatMessagePrompt`'s query key and staleTime rather than that hook
 * itself, which fetches one fixed message: a turn has as many prompts to read
 * as it made Bedrock calls, which React's hook rules will not let a component
 * loop over. Sharing the key keeps a prompt the debug view already fetched
 * from being fetched twice.
 */
export function MessageKnowledgeGraphButton({
  threadId,
  assistantSeqsOfTurn,
}: MessageKnowledgeGraphButtonProps) {
  const [open, setOpen] = React.useState(false);
  const { adminService } = useDomainServices();
  const results = useQueries({
    queries: assistantSeqsOfTurn.map((seq) => ({
      queryKey: ["adminChatPrompt", threadId, seq],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        adminService.getChatMessagePrompt(threadId, seq, {
          abortSignal: signal,
        }),
      enabled: open,
      staleTime: 30000,
    })),
  });

  const isLoading = results.some((result) => result.isLoading);
  const error = results.find((result) => result.error)?.error;
  const loaded = results.some((result) => result.data !== undefined);
  // Flattened in call order, which is the order the retrievals ran: the
  // per-turn injection on the turn's first call, then each tool search on the
  // call that followed it.
  const traces = results.flatMap(
    (result) => result.data?.knowledge_graph_traces ?? [],
  );

  return (
    <>
      <RobotoTooltip title={LABEL}>
        <span>
          <RobotoIconButton
            aria-label={LABEL}
            className={styles.iconButton}
            onClick={() => {
              setOpen(true);
            }}
          >
            <AccountTreeOutlinedIcon className={styles.icon} />
          </RobotoIconButton>
        </span>
      </RobotoTooltip>
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        // The candidates table is a dense, many-column data grid — "md"
        // left it cramped against the dialog edge before the wrapper's own
        // horizontal scroll ever kicked in. "lg" gives it real room first.
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Knowledge graph retrieval</DialogTitle>
        <DialogContent className={styles.content}>
          {isLoading && (
            <div className={styles.state}>
              Loading knowledge-graph retrieval…
            </div>
          )}
          {/* One failed reconstruction still leaves the traces that did load
              worth showing, so the error reads as a partial-coverage warning
              rather than replacing the section. */}
          {error && (
            <div className={styles.state}>Failed to load: {error.message}</div>
          )}
          {/* Reuses KnowledgeGraphSection's own empty-state copy when the turn
              captured no traces at all — nothing to duplicate here. */}
          {loaded && <KnowledgeGraphSection traces={traces} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
